import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "interactiveMap" }
const _hoisted_2 = { class: "h5" }
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = {
  key: 0,
  class: "routeInfoWindow"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = ["src", "alt"]
const _hoisted_7 = { key: 0 }
const _hoisted_8 = ["src", "alt"]

import { useRoutesStore } from '@components/store';
import {
  onMounted, onUnmounted, ref, watchEffect,
} from 'vue';
import {
  InfoWindow,
} from 'vue3-google-map';
import {
  GoogleMapsRoute, LatLng, MapLocation, MapPort,
} from '../../types';
import {
  Map, FilterMapButton, Icon, RouteDetail,
  MoreInfoPanel,
} from '../..';
import { SMALL_DESKTOP_VIEWPORT_WIDTH } from '../../config';


export default /*@__PURE__*/_defineComponent({
  __name: 'InteractiveMap',
  props: {
    isActive: { type: Boolean }
  },
  emits: ['closeExploreMap'],
  setup(__props: any) {





const routesStore = useRoutesStore();

const mapRef = ref();
const mapCenter = ref<LatLng>({ lat: 56.30867, lng: -5.958157 });
const ports = ref();
const destinations = ref(routesStore.getAllMapDestinationsNoStatus);
const routes = ref();

const destinationLabel = ref<MapLocation>();
const portLabel = ref<MapPort>();
const routeLabel = ref<GoogleMapsRoute>();

const destinationClicked = ref<MapLocation>();
const portClicked = ref<MapPort>();
const routeClicked = ref<GoogleMapsRoute>();

const isOverlayOpen = ref(false);

const isSmallScreenWidth = window.innerWidth <= SMALL_DESKTOP_VIEWPORT_WIDTH;
const mainNav = document.querySelector<HTMLElement>('.mainNavigation');

const addStopScroll = () => {
  if (!document.querySelector('html')?.classList.contains('stopScroll')) {
    document.querySelector('html')?.classList.add('stopScroll');
    if (!mainNav) return;
    mainNav.style.display = 'none';
  }
};

const removeStopScroll = () => {
  if ((document.querySelector('html')?.classList.contains('stopScroll'))) {
    document.querySelector('html')?.classList.remove('stopScroll');
    if (!mainNav) return;
    mainNav.style.display = 'block';
  }
};

onMounted(async () => {
  await routesStore.requestAllRoutesMapCoords();

  if (window.innerWidth <= SMALL_DESKTOP_VIEWPORT_WIDTH) {
    addStopScroll();
  }

  window.addEventListener('resize', () => {
    if (window.innerWidth <= SMALL_DESKTOP_VIEWPORT_WIDTH) {
      addStopScroll();
    }
    if (window.innerWidth >= SMALL_DESKTOP_VIEWPORT_WIDTH) {
      removeStopScroll();
    }
  });
});

onUnmounted(() => {
  removeStopScroll();

  window.removeEventListener('resize', () => {
    if (window.innerWidth <= SMALL_DESKTOP_VIEWPORT_WIDTH) {
      addStopScroll();
    }
    if (window.innerWidth >= SMALL_DESKTOP_VIEWPORT_WIDTH) {
      removeStopScroll();
    }
  });
});

const resetLabels = () => {
  destinationLabel.value = undefined;
  portLabel.value = undefined;
  routeLabel.value = undefined;
};

const resetClickedItems = () => {
  destinationClicked.value = undefined;
  portClicked.value = undefined;
  routeClicked.value = undefined;
};

const toggleMapView = (item, isChecked) => {
  switch (item) {
    case 'destinations':
      if (isChecked) destinations.value = routesStore.getAllMapDestinationsNoStatus;
      else destinations.value = undefined;
      break;
    case 'ports':
      if (isChecked) ports.value = routesStore.getAllMapPortsNoStatus;
      else ports.value = undefined;
      break;
    default:
      if (isChecked) routes.value = routesStore.getAllMapRoutesNoStatus;
      else routes.value = undefined;
  }
};

const fitToBounds = (east: number, north: number, south: number, west: number, padding = 0) => {
  const gmap = mapRef.value?.map;

  gmap.fitBounds({
    east,
    north,
    south,
    west,
  }, padding);
};

const centerPinOnMap = (latlng: LatLng) => {
  const east = latlng.lng + 0.5;
  const north = latlng.lat + 0.5;
  const south = isSmallScreenWidth ? latlng.lat - 1.5 : latlng.lat - 0.5;
  const west = isSmallScreenWidth ? latlng.lng - 0.5 : latlng.lng - 1.5;

  fitToBounds(east, north, south, west);
};

const handlePortClick = (port: MapPort) => {
  isOverlayOpen.value = true;
  resetLabels();
  resetClickedItems();

  portClicked.value = port;

  centerPinOnMap(port.options.position);
};

const handleDestinationClick = (destination: MapLocation) => {
  isOverlayOpen.value = true;
  resetLabels();
  resetClickedItems();

  destinationClicked.value = destination;

  centerPinOnMap(destination.options.position);
};

const handleRouteClick = (route: GoogleMapsRoute) => {
  isOverlayOpen.value = true;
  resetLabels();
  resetClickedItems();

  routeClicked.value = route;

  const maxLat = Math.max(...route.ports.map((p) => (p.latitude ? p.latitude : 0)));
  const minLat = Math.min(...route.ports.map((p) => (p.latitude ? p.latitude : 0)));
  const maxLng = Math.max(...route.ports.map((p) => (p.longitude ? p.longitude : 0)));
  const minLng = Math.min(...route.ports.map((p) => (p.longitude ? p.longitude : 0)));

  const west = isSmallScreenWidth ? minLng : minLng - 1;
  const south = isSmallScreenWidth ? minLat - 0.5 : minLat;
  fitToBounds(maxLng, maxLat, south, west);
};

const getLabelOffset = () => {
  const gmap = mapRef.value?.map;
  const offset = -25 + (gmap ? gmap.getZoom() : 0);
  return offset;
};

watchEffect(() => {
  destinations.value = routesStore.getAllMapDestinationsNoStatus;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["mobileTop", {panelActive: _ctx.isActive}])
    }, [
      _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t('Explore map')), 1),
      _createElementVNode("button", {
        "aria-label": _ctx.$t('Close explore map panel'),
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('closeExploreMap')))
      }, [
        _createVNode(_unref(Icon), {
          "icon-name": "close",
          md: ""
        })
      ], 8, _hoisted_3)
    ], 2),
    _createVNode(_unref(Map), {
      center: mapCenter.value,
      ports: ports.value,
      destinations: destinations.value,
      "destination-hover-item": destinationLabel.value,
      "destination-click-item": destinationClicked.value,
      "port-hover-item": portLabel.value,
      "port-click-item": portClicked.value,
      routes: routes.value,
      "route-hover-item": routeLabel.value,
      "route-click-item": routeClicked.value,
      onUpdateMapRef: _cache[1] || (_cache[1] = (map) => mapRef.value = map),
      onMapClicked: resetLabels,
      onDestinationHovered: _cache[2] || (_cache[2] = (destination) => destinationLabel.value = destination),
      onDestinationLeave: _cache[3] || (_cache[3] = ($event: any) => (destinationLabel.value = undefined)),
      onDestinationClicked: _cache[4] || (_cache[4] = (destination) => handleDestinationClick(destination)),
      onPortHovered: _cache[5] || (_cache[5] = (port) => portLabel.value = port),
      onPortLeave: _cache[6] || (_cache[6] = ($event: any) => (portLabel.value = undefined)),
      onPortClicked: _cache[7] || (_cache[7] = (port) => handlePortClick(port)),
      onRouteHovered: _cache[8] || (_cache[8] = (route) => routeLabel.value = route),
      onRouteLeave: _cache[9] || (_cache[9] = ($event: any) => (routeLabel.value = undefined)),
      onRouteClicked: _cache[10] || (_cache[10] = (route) => handleRouteClick(route))
    }, {
      routeInfoWindow: _withCtx(() => [
        (routeLabel.value && routeLabel.value !== routeClicked.value && !isSmallScreenWidth)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_unref(RouteDetail), {
                ports: [routeLabel.value.originPort, routeLabel.value.destinationPort],
                "destination-name": routeLabel.value.location
              }, null, 8, ["ports", "destination-name"])
            ]))
          : _createCommentVNode("", true)
      ]),
      customPortIcon: _withCtx(() => [
        _createVNode(_unref(Icon), {
          class: "mapIcon",
          "icon-name": "anchorPin",
          lg: "",
          alt: ""
        })
      ]),
      customPortIconHover: _withCtx(() => [
        _createVNode(_unref(Icon), {
          class: "mapIcon",
          "icon-name": "anchorPinHover",
          lg: "",
          alt: ""
        })
      ]),
      customPortIconSelected: _withCtx(() => [
        _createVNode(_unref(Icon), {
          class: "mapIcon selected",
          "icon-name": "anchorPinHover",
          lg: "",
          alt: ""
        })
      ]),
      portInfoWindow: _withCtx(() => [
        (portLabel.value && portLabel.value !== portClicked.value && !isSmallScreenWidth)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(InfoWindow), {
                class: "infoWindow",
                options: {
              position: {
                lat: portLabel.value.options.position.lat,
                lng: portLabel.value.options.position.lng,
              },
              pixelOffset: {
                width: 0,
                height: getLabelOffset(),
              },
            }
              }, {
                default: _withCtx(() => [
                  _createElementVNode("img", {
                    src: portLabel.value.image,
                    alt: _ctx.$t('Port image')
                  }, null, 8, _hoisted_6),
                  _createElementVNode("p", null, _toDisplayString(portLabel.value.name), 1)
                ]),
                _: 1
              }, 8, ["options"])
            ]))
          : _createCommentVNode("", true)
      ]),
      customDestinationIcon: _withCtx(() => [
        _createVNode(_unref(Icon), {
          class: "mapIcon",
          "icon-name": "redMapPin",
          lg: "",
          alt: ""
        })
      ]),
      customDestinationIconHover: _withCtx(() => [
        _createVNode(_unref(Icon), {
          class: "mapIcon",
          "icon-name": "redMapPinHover",
          lg: "",
          alt: ""
        })
      ]),
      customDestinationIconSelected: _withCtx(() => [
        _createVNode(_unref(Icon), {
          class: "mapIcon selected",
          "icon-name": "redMapPinHover",
          lg: "",
          alt: ""
        })
      ]),
      destinationInfoWindow: _withCtx(() => [
        (destinationLabel.value && destinationLabel.value !== destinationClicked.value
            && !isSmallScreenWidth)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createVNode(_unref(InfoWindow), {
                class: "infoWindow",
                options: {
              position: {
                lat: destinationLabel.value.options.position.lat,
                lng: destinationLabel.value.options.position.lng,
              },
              pixelOffset: {
                width: 0,
                height: getLabelOffset(),
              },
            }
              }, {
                default: _withCtx(() => [
                  _createElementVNode("img", {
                    src: destinationLabel.value.image,
                    alt: _ctx.$t('Destination image')
                  }, null, 8, _hoisted_8),
                  _createElementVNode("p", null, _toDisplayString(destinationLabel.value.name), 1)
                ]),
                _: 1
              }, 8, ["options"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["center", "ports", "destinations", "destination-hover-item", "destination-click-item", "port-hover-item", "port-click-item", "routes", "route-hover-item", "route-click-item"]),
    _createVNode(_unref(FilterMapButton), { onToggleCheckedItems: toggleMapView }),
    _createVNode(_unref(MoreInfoPanel), {
      "is-open": isOverlayOpen.value,
      "close-callback": () => {
        isOverlayOpen.value = false;
        resetClickedItems();
      },
      destination: destinationClicked.value,
      port: portClicked.value,
      route: routeClicked.value
    }, null, 8, ["is-open", "close-callback", "destination", "port", "route"])
  ]))
}
}

})